import { Trans, useTranslation } from "next-i18next";
import React from "react";
import Image from "next/image";
import { useRouter } from "next/router";

import {
  AMSTERDAM_PAS_PORTAL_BASE_URL,
  AMSTERDAM_PAS_PORTAL_TEST_BASE_URL,
} from "~config/cardapi";
import { apiFetch } from "~lib/apiFetch";
import { resolveUserEmail } from "~lib/auth/utils/resolveUserEmail";
import { formatCurrency } from "~utils/format";
import formatDate from "~utils/formatDate";

import Button from "~components/Button";
import DynamicContent from "~modules/DynamicContent";
import Accordion from "~ts_components/Accordion/Accordion";
import DuoColorText from "~ts_components/DuoColorText/DuoColorText";
import ShopCardPreview from "~ts_components/ShopCardPreview/ShopCardPreview";
import { ShopCardContentLine } from "~ts_components/ShopCardPreview/types";

import { ExtendedUserData, VerifiedAuthenticatedUser } from "~types/types";

type AccountSettingsPageProps = {
  accountSettingsPage: {
    id: string;
    title: string;
    content: React.ReactNode[];
  };
  user: VerifiedAuthenticatedUser;
  userData: ExtendedUserData;
};

type InfoSectionProps = {
  title: string;
  data: ReadonlyArray<{
    id: string;
    label: string;
    content?: string | null;
    actions?: React.ReactNode[];
  }>;
};

const InfoSection = ({ title, data }: InfoSectionProps) => (
  <div>
    <h2 className="text-header-4 mb-4">{title}</h2>
    <ul className="flex flex-col gap-y-4">
      {data.map(({ id, label, content, actions }) => (
        <li key={id} className="flex flex-col gap-y-2">
          <span className="text-body-large-bold">{label}</span>
          {content && <span className="text-body">{content}</span>}
          {actions && (
            <div className="flex flex-col items-start gap-y-2">
              {actions.map((action, idx) => (
                <React.Fragment key={idx}>{action}</React.Fragment>
              ))}
            </div>
          )}
        </li>
      ))}
    </ul>
  </div>
);

const AccountSettingsPageTemplate = ({
  accountSettingsPage: { id, title, content },
  user,
  userData,
}: AccountSettingsPageProps): React.ReactElement => {
  const { locale } = useRouter();
  const { t } = useTranslation();

  const ACCOUNT_OWNER_LINES = [
    {
      id: "first-name",
      label: t("account:firstName"),
      content: user.first_name,
    },
    {
      id: "last-name",
      label: t("account:lastName"),
      content: user.last_name,
    },
    {
      id: "email",
      label: t("account:email"),
      content: resolveUserEmail(user),
    },
  ];

  const CURRENT_SUBSCRIPTION_LINES = userData.voucher
    ? [
        {
          id: "order-number",
          label: t("shop:orderNumber"),
          content: userData.voucher.Vouchernr,
        },
        {
          id: "account-creation-date",
          label: t("shop:accountCreationDate"),
          content: formatDate(userData.voucher.Createdate, locale),
        },
      ]
    : [];

  const PAYMENT_DETAILS_LINES = [
    userData.product?.title && {
      id: "subscription-type",
      label: t("shop:subscription"),
      content: userData.product.title,
    },
    userData.mollieSubscription?.nextPaymentDate && {
      id: "subscription-next-payment",
      label: t("shop:nextPaymentDate"),
      content: formatDate(
        userData.mollieSubscription.nextPaymentDate,
        locale,
        "PPP",
      ),
    },
    userData.mollieSubscription?.amount && {
      id: "subscription-next-payment-amount",
      label: t("shop:nextPaymentAmount"),
      content: formatCurrency(userData.mollieSubscription.amount.value),
    },
  ].filter(Boolean) as ReadonlyArray<{
    id: string;
    label: string;
    content: string;
  }>;

  const amsterdamPasPortalBaseUrl =
    userData.molliePayment?.mode === "test"
      ? AMSTERDAM_PAS_PORTAL_TEST_BASE_URL
      : AMSTERDAM_PAS_PORTAL_BASE_URL;

  const addToWalletIosUrl = userData.voucher?.Vouchernr
    ? `${amsterdamPasPortalBaseUrl}/pass/${userData.voucher.Vouchernr}/apple`
    : "";
  const addToWalletAndroidUrl = userData.voucher?.Vouchernr
    ? `${amsterdamPasPortalBaseUrl}/pass/${userData.voucher.Vouchernr}/android`
    : "";

  const resendEmailUrl = userData.voucher?.Vouchernr
    ? "/account/resend-voucher-email"
    : "";

  const showAddToWalletIos = addToWalletIosUrl !== "";
  const showAddToWalletAndroid = addToWalletAndroidUrl !== "";

  async function onResendEmail(): Promise<void> {
    if (!resendEmailUrl) {
      return;
    }
    try {
      await apiFetch(resendEmailUrl, {
        method: "POST",
      });
    } catch (error) {
      console.error("Error resending email:", error);
      alert("Error resending email. Please try again later.");
      return;
    }
    alert("Email sent successfully!");
  }

  return (
    <div className="lg:bg-grey-light -mt-4 py-8 max-md:px-2 md:py-16">
      <h1 className="text-header-1 mb-10 text-center">{title}</h1>
      <div className="max-w-300 mx-auto rounded-lg bg-white p-2 lg:p-16">
        {/* Main two-column grid layout */}
        <div className="grid grid-cols-1 gap-y-4 lg:grid-cols-12 lg:gap-x-5">
          {/* Left column - Account details */}
          <section className="order-2 flex w-full flex-col gap-y-4 lg:order-1 lg:col-span-7">
            <InfoSection title="Over jou" data={ACCOUNT_OWNER_LINES} />
            <div className="layout-divider my-2 lg:my-4" />
            <InfoSection
              title={t("account:aboutAmsterdamPas")}
              data={CURRENT_SUBSCRIPTION_LINES}
            />
            {PAYMENT_DETAILS_LINES.length > 0 && (
              <>
                <div className="layout-divider my-2 lg:my-4" />
                <InfoSection
                  title={t("account:paymentDetails")}
                  data={PAYMENT_DETAILS_LINES}
                />
              </>
            )}
          </section>

          {/* Right column - current subscription card */}
          <section className="order-1 mb-4 flex w-full flex-col gap-y-4 lg:order-2 lg:col-span-5">
            {userData.product && (
              <ShopCardPreview
                product={{
                  id: userData.product.id,
                  title: userData.product.title as string,
                  price: userData.product.price as number,
                  frequency: userData.product.productFrequency as string,
                }}
                contentItems={
                  [
                    {
                      id: "name",
                      label: t("account:amsterdamPasOwner"),
                      content: `${userData.customer.first_name} ${userData.customer.last_name}`,
                    },
                    userData.voucher && {
                      id: "orderNumber",
                      label: t("shop:orderNumber"),
                      content: userData.voucher.Vouchernr,
                    },
                    userData.voucher?.Expirydate && {
                      id: "valid-until",
                      label: t("shop:validUntil"),
                      content: userData.voucher.Expirydate
                        ? formatDate(userData.voucher.Expirydate, locale, "PPP")
                        : "",
                    },
                  ].filter(Boolean) as ShopCardContentLine[]
                }
              />
            )}
            {userData.voucher && (
              <div className="border-grey-border box-shadow-card mt-4 rounded-xl border p-6">
                <h3 className="text-header-5 mb-2">
                  <DuoColorText
                    text={t("account:useUitPasTitle")}
                    emphasis={t("account:useUitPasEmphasis")}
                  />
                </h3>
                <div className="flex flex-col gap-4 md:flex-row md:gap-6 lg:hidden">
                  {showAddToWalletIos && (
                    <a
                      href={addToWalletIosUrl}
                      className="flex items-center justify-center"
                    >
                      <Image
                        src="/images/apple-wallet.svg"
                        alt={t("account:addAppleWallet")}
                        width={166}
                        height={52}
                      />
                    </a>
                  )}
                  {showAddToWalletAndroid && (
                    <a
                      href={addToWalletAndroidUrl}
                      className="flex items-center justify-center"
                    >
                      <Image
                        src="/images/google-wallet.svg"
                        alt={t("account:addGoogleWallet")}
                        width={171}
                        height={53}
                      />
                    </a>
                  )}
                </div>
                <Accordion
                  bellowType="card"
                  items={[
                    {
                      id: "uitpas-wallet-info",
                      title: t("account:addUitpasToWalletTitle"),
                      content: (
                        <div className="flex flex-col items-start gap-y-4">
                          <Trans
                            t={t}
                            i18nKey="account:addUitpasToWalletDescription"
                          >
                            <p className="text-body">
                              Wil je je Uitpas makkelijk gebruiken zonder gedoe?
                              Voeg &apos;m toe aan je Apple Wallet of Google
                              Wallet! In de bevestigingsmail na je aankoop vind
                              je een link om je Uitpas eenvoudig toe te voegen
                              aan Apple Wallet of Google Wallet.
                            </p>
                            <p className="text-body">
                              Mail niet meer terug te vinden? Geen probleem! Je
                              kunt de e-mail altijd opnieuw versturen.
                            </p>
                          </Trans>

                          <Button
                            variant="outline"
                            label={t("account:sendEmailAgain")}
                            onClick={onResendEmail}
                          >
                            {t("account:sendEmailAgain")}
                          </Button>
                        </div>
                      ),
                      variant: "card",
                    },
                  ]}
                  className="mt-4"
                />
              </div>
            )}
          </section>
        </div>
        <div className="layout-divider" />
        {Boolean(content?.length) && (
          <DynamicContent content={content} parentPageId={id} />
        )}
      </div>
    </div>
  );
};

export default AccountSettingsPageTemplate;
